import { Location } from '@angular/common';
import { inject, Injectable, OnDestroy, OnInit } from '@angular/core';
import {
  AngularFirestore,
  DocumentChangeAction,
} from '@angular/fire/compat/firestore';
import { Timestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { intersects } from 'radash';
import { combineLatest, EMPTY, from, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { Group, GroupType } from '../../models/group.model';
import { Member } from '../../models/member.model';
import { SchoolMemberType } from '../../models/member.school.model';
import { UserModel } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';
import { ConnectionService } from '../../services/connection.service';
import { GroupService } from '../../services/group.service';
import { AiService } from '../../services/ai.service';
import { AccountingService } from '../../services/accounting.service';
import * as groupSelector from '../../state/selectors/group.selector';
import * as groupActions from '../actions/group.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';

@Injectable()
export class GroupEffects implements OnInit, OnDestroy {
  actions$ = inject(Actions);
  private logger = inject(NGXLogger);
  private connectionService = inject(ConnectionService);
  store: Store<AppState> = inject(Store<AppState>);
  private groupService = inject(GroupService);
  private authService = inject(AuthService);
  private aiService = inject(AiService);
  private accountingService = inject(AccountingService);
  toastr: ToastrService = inject(ToastrService);
  private afs = inject(AngularFirestore);
  router: Router = inject(Router);

  user: UserModel | null | undefined;

  private logout$ = new Subject<void>();

  constructor(private location: Location) {}

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.logout$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  initGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.authenticated),
      switchMap(({ user }) => of(groupActions.loadGroups()))
    )
  );

  loadGroups$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.authenticated), // Not using 'loadGroups' as it does not have the uid
      switchMap(({ user }) => {
        if (!user.uid) return EMPTY;

        // Combine with isConnected$
        return this.connectionService.isConnected$.pipe(
          filter((isConnected) => isConnected), // Only proceed if connected
          take(1), // Take only the first `true` value to avoid multiple triggers
          map(() => user)
        );
      }),
      switchMap((user) => {
        this.logout$.next();
        return this.afs
          .collection<Member>('memberships', (ref) =>
            ref.where('memberId', '==', user.uid)
          )
          .stateChanges()
          .pipe(takeUntil(this.logout$));
      }),
      switchMap((memberships: DocumentChangeAction<Member>[]) => {
        // console.log(
        //   `loadGroups$ :: Total memberships: ${memberships.length}. Change triggered.`
        // );
        const memberActions = memberships.flatMap((doc): Action[] => {
          if (doc.type === 'removed') {
            // Membership doc ids are `${groupId}_${uid}`; the group entity is
            // keyed by groupId. Reset its cached children, then drop it so
            // guards stop granting the old role.
            const groupId =
              doc.payload.doc.data()?.groupId ??
              doc.payload.doc.id.split('_')[0];
            this.logger.debug(`loadGroups$ :: membership removed for group ${groupId}`);
            return [
              groupActions.clearFromStore({ id: groupId }),
              groupActions.removeFromStore({ id: groupId }),
            ];
          }
          const data = doc.payload.doc.data();
          const group = data as Member;
          group.id = data.groupId;
          return [groupActions.getMemberGroup({ member: group })];
        });
        return [...memberActions, groupActions.loadGroupsComplete()];
      }),
      catchError((error) => {
        return of(groupActions.loadGroupsComplete());
      })
    )
  );

  getGroup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.getMemberGroup),
      mergeMap(({ member }) =>
        this.connectionService.isConnected$.pipe(
          filter((isConnected) => isConnected), // Only proceed if connected
          take(1),
          switchMap(() =>
            this.afs
              .doc<Group>('groups/' + member.groupId)
              .get()
              .pipe(
                map((snapshot) => {
                  if (!snapshot.exists) return null;
                  let group: Group = snapshot.data() as Group;
                  if (group && !group.groupType) {
                    group.groupType = GroupType.church;
                  }
                  group.memberType = member.memberType;
                  group.memberId = member.memberId;
                  group.memberStatus = member.status;
                  group.memberJoinDate = member.created;
                  group.memberColor = member.memberColor;

                  group.gradeId = member.gradeId;
                  group.gradeStatus = member.gradeStatus;
                  group.gradeName = member.gradeName;
                  group.gradeYear = member.gradeYear;

                  group.studentId = member.studentId;
                  group.parentId = member.parentId;
                  group.parentName = member.parentName;

                  group.id = snapshot.id;
                  return group;
                }),
                mergeMap((group) => {
                  if (group != null) {
                    return of(groupActions.addOneToStore({ group }));
                  }
                  return EMPTY;
                })
              )
          )
        )
      )
    )
  );

  getChurchGroupStats$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select, groupActions.getGroupStats),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ groupId }, group]) => {
        if (group?.groupType === GroupType.school) return EMPTY;
        if (!group || group.statsLoaded) return EMPTY;

        return this.connectionService.isConnected$.pipe(
          filter((isConnected) => isConnected),
          take(1),
          switchMap(() =>
            this.afs
              .collection<any>('group_stats')
              .doc(groupId)
              .get()
              .pipe(
                takeUntil(this.logout$),
                map((snapshot) => ({ snapshot, groupId }))
              )
          )
        );
      }),
      switchMap(({ snapshot, groupId }) => {
        if (!snapshot.exists) {
          return of(groupActions.getGroupStatsDone({ group: null }));
        }

        const record: any = snapshot.data();
        let g: Group = {
          id: groupId,
          activeMembers: record.active_members,
          activePrayers: record.active_prayers,
          activeEvents: record.active_events,
          adminMembers: record.admins,
          invitedMembers: record.invited_members,
          requestedMembers: record.requested_members,
          new_members: record.new_members,
          statsLoaded: true,
        } as Group;

        return of(groupActions.getGroupStatsDone({ group: g }));
      }),
      catchError((error) => {
        console.log(`getChurchGroupStats$ :: Error: ${JSON.stringify(error)}`);
        return of(groupActions.getGroupStatsDone({ group: null }));
      })
    )
  );

  getGroupStats$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        groupActions.select,
        groupActions.getGroupStats,
        groupActions.reloadGroupStats
      ),
      switchMap(({ groupId }) => {
        if (!groupId) return EMPTY;
        return this.store.select(groupSelector.getGroupById(groupId)).pipe(
          filter(
            (group) =>
              !!group &&
              group.groupType === GroupType.school &&
              group.academicYear !== undefined
          ),
          take(1),
          switchMap((group) => {
            return this.connectionService.isConnected$.pipe(
              filter((isConnected) => isConnected),
              take(1),
              switchMap(() =>
                this.afs
                  .collection('groups')
                  .doc(groupId)
                  .collection<any>('group_stats')
                  .doc(`${groupId}_${group!.academicYear}`)
                  .valueChanges()
                  .pipe(
                    takeUntil(
                      this.actions$.pipe(
                        ofType(
                          groupActions.unselect,
                          groupActions.clear,
                          userActions.logout
                        )
                      )
                    ),
                    map((record: any) => {
                      if (!record) {
                        return groupActions.getGroupStatsDone({ group: null });
                      }

                      let g: Group = {
                        id: groupId,
                        activeMembers: record.active_members,
                        activePrayers: record.active_prayers,
                        activeEvents: record.active_events,
                        adminMembers: record.admins,
                        invitedMembers: record.invited_members,
                        requestedMembers: record.requested_members,
                        teachers: record.teachers,
                        students: record.students,
                        parents: record.parents,
                        grades: record.grades,
                        new_members: record.new_members,
                        statsLoaded: true,
                      } as Group;

                      return groupActions.getGroupStatsDone({ group: g });
                    }),
                    catchError((error) => {
                      console.log(
                        `getGroupStats$ :: Error: ${JSON.stringify(error)}`
                      );
                      return of(groupActions.getGroupStatsDone({ group: null }));
                    })
                  )
              )
            );
          })
        );
      })
    )
  );

  createBillingAccount$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.createBillingAccount),
      switchMap(() =>
        combineLatest([
          this.store.select((state) => state.user.user).pipe(take(1)),
          this.connectionService.isConnected$.pipe(take(1)),
        ]).pipe(
          switchMap(([user, isConnected]) => {
            if (!isConnected) {
              this.toastr.error(
                `You are offline. Please check your connection and try again.`,
                'Error Creating Billing Account'
              );
              return of(
                groupActions.createBillingAccountFailure({
                  error: 'Offline',
                })
              );
            }
            this.logger.debug(
              'createBillingAccount :: User online, proceeding...'
            );

            if (user?.name === undefined || user?.email === undefined) {
              this.toastr.error(
                `User details name and email are required to create a billing account. Please update your profile and try again.`,
                'Error Creating Billing Account'
              );
              return of(
                groupActions.createBillingAccountFailure({
                  error: 'User details missing',
                })
              );
            }
            this.logger.debug(
              'createBillingAccount :: User record found, proceeding to invoke backend function'
            );

            return from(
              this.groupService.createBillingAccount({
                name: user.name,
                email: user.email,
              })
            ).pipe(
              map(() => {
                this.toastr.success(
                  `Billing account created successfully.`,
                  'Billing Account Created'
                );
                this.logger.info('Billing account created successfully');
                return groupActions
                  .createBillingAccountSuccess
                  // groupId: groupId,
                  // nextAction: BillingActions.SETUP_PAYMENTS,
                  ();
              }),
              catchError((error) => {
                this.toastr.error(
                  `Error occurred while creating the billing account. Error is: ${error.toString()}`,
                  'Error Creating Billing Account'
                );
                this.logger.error(
                  'Error creating billing account',
                  error.toString()
                );
                return of(groupActions.createBillingAccountFailure({ error }));
              })
            );
          })
        )
      )
    )
  );

  // TODO: Effect to update grade stats if a student is added or removed
  // reloadGroupStats$: Observable<Action> = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(memberActions.updateSuccess),
  //     withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
  //     switchMap(([action, group]) => {
  //       if (group?.groupType !== GroupType.school) return EMPTY;

  //       if (
  //         !intersects(group?.memberType as SchoolMemberType[], [
  //           SchoolMemberType.president,
  //           SchoolMemberType.principal,
  //         ])
  //       ) {
  //         return EMPTY;
  //       }

  //       if (!group || group.statsLoaded) return EMPTY;

  //       return this.connectionService.isConnected$.pipe(
  //         filter((isConnected) => isConnected),
  //         take(1),
  //         switchMap(() =>
  //           this.afs
  //             .collection('groups')
  //             .doc(action.member.groupId)
  //             .collection<any>('group_stats')
  //             .doc(`${action.member.groupId}_${group.academicYear}`)
  //             .get()
  //             .pipe(
  //               takeUntil(this.logout$),
  //               map((snapshot) => ({ snapshot, action.member.groupId }))
  //             )
  //         )
  //       );
  //     }),
  //     switchMap(({ snapshot, groupId }) => {
  //       if (!snapshot.exists) {
  //         return of(groupActions.getGroupStatsDone({ group: null }));
  //       }

  //       const record: any = snapshot.data();
  //       let g: Group = {
  //         id: groupId,
  //         activeMembers: record.active_members,
  //         activePrayers: record.active_prayers,
  //         activeEvents: record.active_events,
  //         adminMembers: record.admins,
  //         invitedMembers: record.invited_members,
  //         requestedMembers: record.requested_members,
  //         teachers: record.teachers,
  //         students: record.students,
  //         parents: record.parents,
  //         grades: record.grades,
  //         new_members: record.new_members,
  //         statsLoaded: true,
  //       } as Group;

  //       return of(groupActions.getGroupStatsDone({ group: g }));
  //     }),
  //     catchError((error) => {
  //       console.log(`getGroupStatistics$ :: Error: ${JSON.stringify(error)}`);
  //       return of(groupActions.getGroupStatsDone({ group: null }));
  //     })
  //   )
  // );

  // Effect to Create Group
  createGroup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.createGroup),
      mergeMap(({ group }) =>
        this.store
          .select((state) => state.user.user)
          .pipe(
            take(1),
            mergeMap((user) => {
              if (!user) {
                this.toastr.error(
                  `Could not find the logged in user. Please login again.`,
                  'Error Creating Group'
                );
                return of(
                  groupActions.createGroupFailure({ error: 'User not found' })
                );
              }

              return this.connectionService.isConnected$.pipe(
                filter((isConnected) => isConnected),
                take(1),
                switchMap(() =>
                  from(this.groupService.createGroup(group, user.name!)).pipe(
                    map(() => {
                      this.router.navigate([`/groups`]);
                      return groupActions.createGroupSuccess();
                    }),
                    catchError((error) => {
                      this.toastr.error(
                        `Error occurred while creating the group. Error is: ${error}`,
                        'Error Creating Group'
                      );
                      return of(groupActions.createGroupFailure({ error }));
                    })
                  )
                )
              );
            })
          )
      )
    )
  );

  // Effect to Update Group
  updateGroup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.update),
      switchMap(({ id, group }) =>
        combineLatest([
          this.store.select((state) => state.user.user).pipe(take(1)),
          this.connectionService.isConnected$.pipe(take(1)),
        ]).pipe(
          switchMap(([user, isConnected]) => {
            if (!isConnected) {
              this.toastr.error(
                `You are offline. Please check your connection and try again.`,
                'Error Updating Group'
              );
              return of(groupActions.updateFailure({ error: 'Offline' }));
            }

            if (!user) {
              this.toastr.error(
                `Could not find the logged in user. Please login again.`,
                'Error Updating Group'
              );
              return of(
                groupActions.updateFailure({ error: 'User not found' })
              );
            }

            const tempGroup = {
              ...group,
              modified: Timestamp.now(),
            };

            return from(this.groupService.updateGroup(id, tempGroup)).pipe(
              map(() => {
                this.location.back();
                const newGroup: Group = {
                  ...group,
                  id,
                  modified: Timestamp.now(),
                };
                return groupActions.updateSuccess({ group: newGroup });
              }),
              catchError((error) => {
                this.toastr.error(
                  `Error occurred while updating the group. Error is: ${error}`,
                  'Error Updating Group'
                );
                return of(groupActions.updateFailure({ error }));
              })
            );
          })
        )
      )
    )
  );

  clearGroupStats$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.clearFromStore),
      switchMap(({ id }) =>
        this.store.select(groupSelector.getGroupById(id)).pipe(
          take(1),
          switchMap((group) => {
            if (group != undefined) {
              let tempGroup = {
                ...group,
                activeMembers: 0,
                activePrayers: 0,
                activeEvents: 0,
                adminMembers: 0,
                invitedMembers: 0,
                requestedMembers: 0,
                teachers: 0,
                students: 0,
                parents: 0,
                grades: 0,
                new_members: 0,
                statsLoaded: false,
                gradeStats: {},
                lessonStats: {},
                gradeStatsLoaded: false,
                lessonStatsLoaded: false,
              } as Group;
              return of(groupActions.updateSuccess({ group: tempGroup }));
            }
            return EMPTY;
          })
        )
      )
    )
  );

  unselectGroup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logout),
      map(() => groupActions.unselect())
    )
  );

  clearGroups$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(groupActions.clear());
      })
    )
  );

  listenToGroupMetadata$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select),
      switchMap(({ groupId }) => {
        if (!groupId) return of(groupActions.loadGroupMetadataSuccess({ groupId: '', metadata: null }));
        return this.groupService.listenToGroupMetadata(groupId).pipe(
          takeUntil(this.actions$.pipe(ofType(groupActions.unselect, groupActions.clear))),
          map((metadata) =>
            groupActions.loadGroupMetadataSuccess({ groupId, metadata })
          ),
          catchError((error) => {
            this.logger.error('Error listening to group metadata:', error);
            return EMPTY;
          })
        );
      })
    )
  );

  listenToGroupTokens$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select),
      switchMap(({ groupId }) => {
        if (!groupId) return of(groupActions.loadGroupTokensSuccess({ groupId: '', tokens: null }));
        return this.groupService.listenToGroupTokens(groupId).pipe(
          takeUntil(this.actions$.pipe(ofType(groupActions.unselect, groupActions.clear))),
          map((tokens) =>
            groupActions.loadGroupTokensSuccess({ groupId, tokens })
          ),
          catchError((error) => {
            this.logger.error('Error listening to group tokens:', error);
            return EMPTY;
          })
        );
      })
    )
  );

  extractReceipt$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.extractReceipt),
      switchMap(({ groupId, gcsPath, mimeType, receiptId }) =>
        from(this.aiService.extractReceipt({ groupId, gcsPath, mimeType })).pipe(
          map((response) =>
            groupActions.extractReceiptSuccess({
              groupId,
              extraction: response.data,
              usage: response.usage,
              receiptId,
            })
          ),
          catchError((error) => {
            this.logger.error('extractReceipt$ Effect Error:', error);
            return of(groupActions.extractReceiptFailure({ error, receiptId }));
          })
        )
      )
    )
  );
}
